import { useEffect, useState, useCallback } from "react";
import { RefreshCw, AlertCircle } from "lucide-react";
import {
  getRetailerHealth,
  describeRetailer,
  type RetailerHealthData,
  type RetailerHealthEntry,
  type RetailerHealthStatus,
} from "../../services/RetailerHealthService";

const DOT: Record<RetailerHealthStatus, string> = {
  healthy: "bg-emerald-500",
  waiting: "bg-gray-300 dark:bg-dark-500",
  behind: "bg-red-500",
  stale: "bg-red-500",
};

function StatusDot({ status }: { status: RetailerHealthStatus }) {
  return (
    <span
      className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${DOT[status]}`}
      aria-hidden
    />
  );
}

function Row({ entry }: { entry: RetailerHealthEntry }) {
  const label = entry.slug.replace(/_/g, "-");
  const suffix = entry.kind === "in_store" ? " in stores" : "";
  return (
    <div className="flex items-center gap-2 py-1 text-xs">
      <StatusDot status={entry.status} />
      <span className="font-medium text-gray-900 dark:text-white truncate">
        {label}
        {suffix}
      </span>
      <span className="text-gray-500 dark:text-dark-300 truncate">
        {describeRetailer(entry)}
      </span>
    </div>
  );
}

/**
 * Live retailer health. Rendered above the daily analytics snapshot and
 * independent of it, so a missing S3 file no longer leaves the tab with
 * nothing to show.
 */
export default function RetailerHealthPanel() {
  const [data, setData] = useState<RetailerHealthData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getRetailerHealth());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load retailer health");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const problems = data?.retailers.filter(
    (r) => r.status === "behind" || r.status === "stale"
  ) ?? [];
  const inStore = data?.retailers.filter(
    (r) => r.kind === "in_store" && r.status !== "behind" && r.status !== "stale"
  ) ?? [];
  const healthyOnline = data?.retailers.filter(
    (r) => r.kind === "online" && r.status === "healthy"
  ) ?? [];
  const waiting = data?.retailers.filter((r) => r.status === "waiting") ?? [];

  return (
    <div className="bg-white dark:bg-dark-800 rounded-2xl ring-1 ring-gray-200 dark:ring-dark-700 shadow-sm p-4">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-[13px] font-bold text-gray-900 dark:text-white">
          Retailer health
        </h3>
        {data && (
          <span className="text-[11px] text-gray-500 dark:text-dark-300">
            live · {new Date(data.generatedAt).toLocaleTimeString()}
          </span>
        )}
        <button
          onClick={() => void load()}
          disabled={loading}
          className="ml-auto p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 disabled:opacity-50"
          aria-label="Refresh retailer health"
        >
          <RefreshCw
            className={`w-3.5 h-3.5 text-gray-500 dark:text-dark-300 ${loading ? "animate-spin" : ""}`}
          />
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          <span>{error}</span>
          <button onClick={() => void load()} className="underline shrink-0">
            Retry
          </button>
        </div>
      )}

      {!error && !data && loading && (
        <p className="text-xs text-gray-500 dark:text-dark-300">Checking every retailer…</p>
      )}

      {data && (
        <>
          {problems.map((r) => (
            <Row key={r.slug} entry={r} />
          ))}
          {inStore.map((r) => (
            <Row key={r.slug} entry={r} />
          ))}

          {!showAll && healthyOnline.length > 0 && (
            <div className="flex items-center gap-2 py-1 text-xs">
              <StatusDot status="healthy" />
              <span className="text-gray-600 dark:text-dark-200">
                {healthyOnline.length} online retailers with products today
              </span>
              <button
                onClick={() => setShowAll(true)}
                className="text-gray-500 dark:text-dark-300 underline"
              >
                show all
              </button>
            </div>
          )}
          {showAll && healthyOnline.map((r) => <Row key={r.slug} entry={r} />)}

          {waiting.length > 0 && (
            <div className="flex items-center gap-2 py-1 text-xs">
              <StatusDot status="waiting" />
              <span className="text-gray-600 dark:text-dark-200">
                {waiting.length} not due yet
              </span>
            </div>
          )}

          <p className="mt-2 pt-2 border-t border-gray-100 dark:border-dark-700 text-[11px] text-gray-500 dark:text-dark-300">
            {problems.length === 0
              ? "All retailers on track."
              : `${problems.length} retailer(s) need attention.`}
          </p>
        </>
      )}
    </div>
  );
}
